import React, { useState } from 'react';
import {
  Input,
  Button,
  Space,
  Row,
  Col,
  Typography,
  message,
  Table,
  Card,
  Statistic,
  Tag,
  Divider,
  Modal,
  Radio
} from 'antd';
import {
  GlobalOutlined,
  ReloadOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  PlayCircleFilled,
  PauseOutlined
} from '@ant-design/icons';
import API from '../services/api'; // 导入API实例

const { Title } = Typography;

const IPPolicyAdjustment = () => {
  const [ip, setIp] = useState(''); // 存储IP地址
  const [loadingBtn, setLoadingBtn] = useState('');
  
  // 新增规则管理相关状态
  const [rules, setRules] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [switchLoading, setSwitchLoading] = useState({});
  
  // 添加规则模态框状态
  const [addRuleModalVisible, setAddRuleModalVisible] = useState(false);
  const [selectedRuleType, setSelectedRuleType] = useState('');

  // 获取过滤规则列表
  const fetchFilterRules = async (ipAddress) => {
    if (!ipAddress) return;
    
    setTableLoading(true);
    try {
      const response = await API.get(`/api/admin/filter-rules?ip=${ipAddress}`);
      if (response.data.code === 0) {
        setRules(response.data.data.rules || []);
        setStatistics(response.data.data.statistics || null);
      } else {
        message.error(response.data.msg || '获取规则列表失败');
        setRules([]);
        setStatistics(null);
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('IP地址参数不能为空');
      } else {
        message.error('获取规则列表时出错');
      }
      setRules([]);
      setStatistics(null);
    }
    setTableLoading(false);
  };

  // 查询规则列表
  const handleQuery = () => {
    if (ip) {
      // IP格式验证
      const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
      if (ipRegex.test(ip)) {
        fetchFilterRules(ip);
      } else {
        message.error('请输入有效的IP地址格式');
      }
    } else {
      message.warning('请先输入IP地址');
    }
  };

  // 规则状态切换
  const handleStatusChange = async (ruleId, ruleType, checked) => {
    setSwitchLoading(prev => ({ ...prev, [ruleId]: true }));
    try {
      const response = await API.put('/api/admin/filter-rule-status', {
        ip: ip,
        type: ruleType,
        rule_id: ruleId,
        status: checked ? 1 : 0
      });
      
      if (response.data.code === 0) {
        message.success(response.data.msg || `规则已${checked ? '启用' : '禁用'}`);
        // 刷新规则列表
        handleQuery();
      } else {
        message.error(response.data.msg || '状态更新失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('参数错误');
      } else {
        message.error('更新规则状态时出错');
      }
    }
    setSwitchLoading(prev => ({ ...prev, [ruleId]: false }));
  };

  // 添加封UDP规则的处理函数
  const handleAddUDPBlock = async () => {
    setLoadingBtn('addUDP');
    try {
      const response = await API.post('/api/admin/block-udp', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封锁UDP流量规则添加成功');
        // 刷新规则列表
        handleQuery();
      } else {
        message.error(response.data.msg || '添加封锁UDP规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('添加封UDP规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 删除封UDP规则的处理函数
  const handleDeleteUDPBlock = async () => {
    setLoadingBtn('delUDP');
    try {
      const response = await API.delete('/api/admin/delete-block-udp', {
        data: { ip }
      });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封锁UDP流量规则删除成功');
        // 刷新规则列表
        handleQuery();
      } else {
        message.error(response.data.msg || '删除规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('删除封锁UDP规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 添加封海外应用规则的处理函数
  const handleAddNonChinaBlock = async () => {
    setLoadingBtn('addNonCN');
    try {
      const response = await API.post('/api/admin/apply-rule-block-non-china', { ip });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封海外规则添加成功');
        // 刷新规则列表
        handleQuery();
      } else {
        message.error(response.data.msg || '添加封海外规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('添加封海外应用规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 删除封海外应用规则的处理函数
  const handleDeleteNonChinaBlock = async () => {
    setLoadingBtn('delNonCN');
    try {
      const response = await API.delete('/api/admin/delete-apply-rule-block-non-china', {
        data: { ip }
      });
      if (response.data.code === 0 || response.data.code === '0') {
        message.success(response.data.msg || '封海外规则删除成功');
        // 刷新规则列表
        handleQuery();
      } else {
        message.error(response.data.msg || '删除封海外规则失败');
      }
    } catch (err) {
      if (err.response?.data?.msg) {
        message.error(err.response.data.msg);
      } else if (err.response?.status === 403) {
        message.error('没有管理员权限');
      } else if (err.response?.status === 400) {
        message.error('请提供IP地址');
      } else {
        message.error('删除封海外规则时出错');
      }
    }
    setLoadingBtn('');
  };

  // 显示添加规则模态框
  const showAddRuleModal = () => {
    setSelectedRuleType('');
    setAddRuleModalVisible(true);
  };

  // 确认添加规则
  const handleAddRule = async () => {
    if (!selectedRuleType) {
      message.warning('请选择要添加的规则类型');
      return;
    }

    if (selectedRuleType === 'udp') {
      await handleAddUDPBlock();
    } else if (selectedRuleType === 'oversea') {
      await handleAddNonChinaBlock();
    }

    setAddRuleModalVisible(false);
    setSelectedRuleType('');
  };

  // 处理IP输入变化
  const handleIpChange = (e) => {
    const newIp = e.target.value;
    setIp(newIp);
    
    // 如果清空IP，则清空规则列表
    if (newIp === '') {
      setRules([]);
      setStatistics(null);
    }
  };

  // 定义表格列
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '规则名称',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type) => {
        const typeMap = {
          'udp': { color: 'blue', text: '封UDP' },
          'oversea': { color: 'blue', text: '封海外' },
          'custom': { color: 'orange', text: '自定义' }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '方向',
      dataIndex: 'direction',
      key: 'direction',
      width: 80,
      render: (direction) => {
        const directionMap = {
          'in': { color: 'blue', text: '接收' },
          'out': { color: 'orange', text: '发送' },
          'both': { color: 'purple', text: '双向' }
        };
        const config = directionMap[direction] || { color: 'default', text: direction };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '行为规则',
      dataIndex: 'action',
      key: 'action',
      width: 80,
      render: (action) => {
        const actionMap = {
          'deny': { color: 'red', text: '拦截' },
          'allow': { color: 'green', text: '允许通过' },
          'drop': { color: 'volcano', text: '丢弃' }
        };
        const config = actionMap[action] || { color: 'default', text: action };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '过滤类型',
      dataIndex: 'type',
      key: 'type_detail',
      width: 100,
      render: (type) => {
        const typeMap = {
          'udp': { color: 'blue', text: 'UDP流量' },
          'oversea': { color: 'blue', text: '海外流量' },
          'tcp': { color: 'green', text: 'TCP协议' },
          'icmp': { color: 'orange', text: 'ICMP协议' },
          'custom': { color: 'purple', text: '自定义' }
        };
        const config = typeMap[type] || { color: 'default', text: type };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => (
        <Tag color={status === 1 ? 'green' : 'red'}>
          {status === 1 ? '已启用' : '已禁用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 250,
      render: (_, record) => (
        <Space>
          <Button 
            type={record.status === 1 ? "default" : "primary"}
            size="small"
            disabled={switchLoading[record.id]} 
            loading={switchLoading[record.id]}
            onClick={() => handleStatusChange(record.id, record.type, record.status === 0)}
            icon={record.status === 1 ? <PauseOutlined /> : <PlayCircleFilled />}
          >
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          {record.type === 'udp' && (
            <Button 
              type="primary"
              danger
              size="small"
              disabled={loadingBtn !== ''} 
              onClick={handleDeleteUDPBlock}
              loading={loadingBtn === 'delUDP'}
              icon={<MinusCircleOutlined />}
            >
              删除
            </Button>
          )}
          {record.type === 'oversea' && (
            <Button 
              type="primary"
              danger
              size="small"
              disabled={loadingBtn !== ''} 
              onClick={handleDeleteNonChinaBlock}
              loading={loadingBtn === 'delNonCN'}
              icon={<GlobalOutlined />}
            >
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          过滤规则管理
        </Title>

        {/* IP输入区域 */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>IP地址：</span>
              <Input
                placeholder="请输入IP地址（例：192.168.1.1）"
                style={{ width: '300px' }}
                value={ip}
                onChange={handleIpChange}
              />
            </div>
            <Space>
              <Button 
                type="primary"
                onClick={handleQuery}
                disabled={!ip}
                loading={tableLoading}
                icon={<ReloadOutlined />}
              >
                查询规则
              </Button>
              <Button 
                disabled={loadingBtn !== '' || !ip} 
                onClick={showAddRuleModal}
                icon={<PlusCircleOutlined />}
              >
                添加规则
              </Button>
            </Space>
          </div>

          {/* 统计信息 */}
          {statistics && (
            <div>
              <Divider />
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="总规则数"
                    value={statistics.total || 0}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="封UDP规则"
                    value={statistics.udp || 0}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="封海外规则"
                    value={statistics.oversea || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="已启用规则"
                    value={statistics.enabled || 0}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </Card>

        {/* 规则列表 */}
        {ip && (
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Title level={4} style={{ margin: 0, marginBottom: '16px' }}>
                过滤规则列表 - {ip}
              </Title>
            </div>

            <Table
              columns={columns}
              dataSource={rules}
              rowKey="id"
              loading={tableLoading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              }}
              scroll={{ x: 1000 }}
            />
          </Card>
        )}

        {/* 添加规则模态框 */}
        <Modal
          title="添加过滤规则"
          open={addRuleModalVisible}
          onCancel={() => {
            setAddRuleModalVisible(false);
            setSelectedRuleType('');
          }}
          onOk={handleAddRule}
          okText="确认添加"
          cancelText="取消"
          confirmLoading={loadingBtn !== ''}
          centered
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '16px' }}>
              <span>选择要添加的规则类型：</span>
            </div>
            <Radio.Group 
              value={selectedRuleType} 
              onChange={(e) => setSelectedRuleType(e.target.value)}
              style={{ width: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <Radio value="udp">
                  <div>
                    <div style={{ fontWeight: 'bold' }}>封UDP规则</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      封禁UDP流量，防止UDP洪水攻击
                    </div>
                  </div>
                </Radio>
                <Radio value="oversea">
                  <div>
                    <div style={{ fontWeight: 'bold' }}>封海外规则</div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      封禁来自海外的所有流量
                    </div>
                  </div>
                </Radio>
              </div>
            </Radio.Group>
          </div>
        </Modal>
        
      </div>
    </div>
  );
};

export default IPPolicyAdjustment;
